import React, { useState, useMemo } from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import { RunComparison, TestRun } from '../types';
import './QuestionComparison.css';

interface QuestionComparisonProps {
  testRuns: TestRun[];
  onCompare?: (comparison: RunComparison) => void;
}

const QuestionComparison: React.FC<QuestionComparisonProps> = ({
  testRuns,
  onCompare
}) => {
  const [selectedRuns, setSelectedRuns] = useState<Set<string>>(new Set());
  const [comparison, setComparison] = useState<RunComparison | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [trendFilter, setTrendFilter] = useState<'all' | 'improving' | 'declining' | 'stable'>('all');

  // Get detailed tooltips for source status values
  const getSourceStatusTooltip = (status: string): string => {
    switch (status) {
      case 'sufficient_context_retrieved':
        return 'The query generated by the bot found information that was sufficient for answering the question. This indicates successful knowledge retrieval on the first attempt.';
      case 'sufficient_context_found':
        return 'The bot didn\'t find enough information initially, but when looking at the ideal answer and asking multiple queries, it was able to find sufficient information. This suggests the initial query could be improved.';
      default:
        return `Status: ${status.replace(/_/g, ' ')}`;
    }
  };

  // Toggle run selection
  const toggleRunSelection = (runId: string) => {
    const newSelection = new Set(selectedRuns);
    if (newSelection.has(runId)) {
      newSelection.delete(runId);
    } else {
      newSelection.add(runId);
    }
    setSelectedRuns(newSelection);
  };

  // Perform comparison when runs are selected
  const performComparison = async () => {
    if (selectedRuns.size < 2) return;

    const selectedTestRuns = testRuns.filter(run => selectedRuns.has(run.id));
    
    try {
      // Import the service dynamically to avoid circular dependencies
      const { testResultsService } = await import('../services/testResultsService');
      const comparisonResult = testResultsService.compareMultipleTestRuns(selectedTestRuns);
      setComparison(comparisonResult);
      onCompare?.(comparisonResult);
    } catch (error) {
      console.error('Failed to perform comparison:', error);
    }
  };

  // Filter question trends based on search and trend filter
  const filteredQuestionTrends = useMemo(() => {
    if (!comparison) return [];

    let filtered = comparison.questionTrends;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(trend => 
        trend.question.toLowerCase().includes(query)
      );
    }

    // Apply trend filter
    if (trendFilter !== 'all') {
      filtered = filtered.filter(trend => trend.trend === trendFilter);
    }

    // Sort by overall change (descending for improvements, ascending for declines)
    return filtered.sort((a, b) => {
      if (trendFilter === 'improving') {
        return b.overallChange - a.overallChange;
      } else if (trendFilter === 'declining') {
        return a.overallChange - b.overallChange;
      }
      return Math.abs(b.overallChange) - Math.abs(a.overallChange);
    });
  }, [comparison, searchQuery, trendFilter]);

  // Get selected question trend data
  const selectedQuestionTrend = useMemo(() => {
    if (!comparison || !selectedQuestion) return null;
    return comparison.questionTrends.find(trend => trend.question === selectedQuestion);
  }, [comparison, selectedQuestion]);

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving': return '📈';
      case 'declining': return '📉';
      case 'stable': return '➡️';
      default: return '❓';
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'improving': return '#10b981';
      case 'declining': return '#ef4444';
      case 'stable': return '#6f747b';
      default: return '#9da2a8';
    }
  };

  return (
    <div className="question-comparison">
      <div className="comparison-header">
        <h3>📊 Question Performance Over Time</h3>
        <p>Compare how individual questions perform across multiple test runs to identify trends and patterns.</p>
      </div>

      {/* Run Selection */}
      <div className="run-selection">
        <h4>Select Test Runs to Compare</h4>
        <div className="run-list">
          {testRuns.map(run => (
            <div 
              key={run.id}
              className={`run-item ${selectedRuns.has(run.id) ? 'selected' : ''}`}
              onClick={() => toggleRunSelection(run.id)}
            >
              <div className="run-info">
                <span className="run-name">{run.filename}</span>
                <span className="run-timestamp">
                  {new Date(run.timestamp).toLocaleDateString()}
                </span>
              </div>
              <div className="run-stats">
                <span className="stat">
                  {run.total_questions} questions
                </span>
                <span className="stat">
                  {Math.round((run.satisfactory_count / run.total_questions) * 100)}% success
                </span>
              </div>
            </div>
          ))}
        </div>
        
        <button 
          className="compare-btn"
          onClick={performComparison}
          disabled={selectedRuns.size < 2}
        >
          {selectedRuns.size < 2 ? 'Select at least 2 runs' : `Compare ${selectedRuns.size} runs`}
        </button>
      </div>

      {/* Comparison Results */}
      {comparison && (
        <div className="comparison-results">
          {/* Overall Trends */}
          <div className="overall-trends">
            <h4>📈 Overall Trends</h4>
            <div className="trend-cards">
              <div className="trend-card">
                <h5>Success Rate</h5>
                <div className="trend-values">
                  {comparison.overallTrends.satisfactoryRate.values.map((value, index) => (
                    <span key={index} className="trend-value">
                      {value.toFixed(1)}%
                    </span>
                  ))}
                </div>
              </div>
              <div className="trend-card">
                <h5>Avg Completeness</h5>
                <div className="trend-values">
                  {comparison.overallTrends.avgCompleteness.values.map((value, index) => (
                    <span key={index} className="trend-value">
                      {value.toFixed(1)}
                    </span>
                  ))}
                </div>
              </div>
              <div className="trend-card">
                <h5>Avg Correctness</h5>
                <div className="trend-values">
                  {comparison.overallTrends.avgCorrectness.values.map((value, index) => (
                    <span key={index} className="trend-value">
                      {value.toFixed(1)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Question Summary */}
          <div className="question-summary">
            <h4>🎯 Question Analysis Summary</h4>
            <div className="summary-stats">
              <div className="summary-card improving">
                <span className="summary-count">{comparison.improvingQuestions.length}</span>
                <span className="summary-label">Improving Questions</span>
              </div>
              <div className="summary-card declining">
                <span className="summary-count">{comparison.decliningQuestions.length}</span>
                <span className="summary-label">Declining Questions</span>
              </div>
              <div className="summary-card failing">
                <span className="summary-count">{comparison.consistentFailures.length}</span>
                <span className="summary-label">Consistent Failures</span>
              </div>
              <div className="summary-card total">
                <span className="summary-count">{comparison.overlappingQuestions.length}</span>
                <span className="summary-label">Overlapping Questions</span>
              </div>
            </div>
          </div>

          {/* Question Filters */}
          <div className="question-filters">
            <div className="filter-row">
              <div className="filter-group">
                <label>Search Questions:</label>
                <input
                  type="text"
                  placeholder="Search for specific questions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
              <div className="filter-group">
                <label>Filter by Trend:</label>
                <select value={trendFilter} onChange={(e) => setTrendFilter(e.target.value as any)}>
                  <option value="all">All Trends</option>
                  <option value="improving">📈 Improving</option>
                  <option value="declining">📉 Declining</option>
                  <option value="stable">➡️ Stable</option>
                </select>
              </div>
            </div>
          </div>

          {/* Question Trends List */}
          <div className="question-trends">
            <h4>📋 Individual Question Trends ({filteredQuestionTrends.length} questions)</h4>
            <div className="trends-list">
              {filteredQuestionTrends.map((trend, index) => (
                <div 
                  key={index}
                  className={`trend-item ${selectedQuestion === trend.question ? 'selected' : ''}`}
                  onClick={() => setSelectedQuestion(trend.question === selectedQuestion ? '' : trend.question)}
                >
                  <div className="trend-header">
                    <span className="trend-icon" style={{ color: getTrendColor(trend.trend) }}>
                      {getTrendIcon(trend.trend)}
                    </span>
                    <span className="trend-question" title={trend.question}>
                      {trend.question.length > 80 ? `${trend.question.substring(0, 80)}...` : trend.question}
                    </span>
                    <span className="trend-change" style={{ color: getTrendColor(trend.trend) }}>
                      {trend.overallChange > 0 ? '+' : ''}{trend.overallChange.toFixed(1)}%
                    </span>
                  </div>
                  <div className="trend-stats">
                    <span>Appears in {trend.dataPoints.length} runs</span>
                    <span className="trend-badge" style={{ backgroundColor: getTrendColor(trend.trend) }}>
                      {trend.trend}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Selected Question Details */}
          {selectedQuestionTrend && (
            <div className="question-details">
              <h4>🔍 Question Performance History</h4>
              <div className="question-text">
                <strong>Question:</strong> {selectedQuestionTrend.question}
              </div>
              
              <div className="performance-timeline">
                {selectedQuestionTrend.dataPoints.map((point, index) => (
                  <div key={index} className="timeline-item">
                    <div className="timeline-header">
                      <span className="timeline-date">
                        {new Date(point.timestamp).toLocaleDateString()}
                      </span>
                      <span className="timeline-run">{point.filename}</span>
                      <span className={`timeline-status ${point.is_satisfactory ? 'satisfactory' : 'unsatisfactory'}`}>
                        {point.is_satisfactory ? (
                          <>
                            <CheckCircle className="h-3 w-3 inline mr-1" />
                            Satisfactory
                          </>
                        ) : (
                          <>
                            <XCircle className="h-3 w-3 inline mr-1" />
                            Unsatisfactory
                          </>
                        )}
                      </span>
                    </div>
                    <div className="timeline-scores">
                      <span className="score">Completeness: {point.completeness}</span>
                      <span className="score">Correctness: {point.correctness}</span>
                      <span className="score">Conciseness: {point.conciseness}</span>
                      <span 
                        className="score source-status"
                        title={getSourceStatusTooltip(point.source_status)}
                      >
                        Status: {point.source_status.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuestionComparison;